import sessionHelper from "./sessionHelper";
import { Client } from "./_clientService";

async function getChallenges(options: any) {
  try {
    let payload: any = {
      pageNo: options?.pageNo,
      limit: options?.pageSize,
      sortKey: "createdAt",
      sortDir: "DESC",
      search: options?.search,
      skipPagination: options?.skipPagination ?? false,
    };
    if (options?.type) payload.type = options.type;
    if (options?.tier) payload.tier = options.tier;
    if (options?.status !== undefined && options?.status !== "")
      payload.status = options.status;
    payload.expired = !!options?.expired;
    payload.archived = !!options?.archived;
    let path = "/challenges/list";
    const resp = await new Client({ path, payload }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function getChallengeInfo(challengeId: any) {
  try {
    const resp = await new Client({
      path: "/challenges/info",
      payload: { challengeId },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

export async function getConvertedRewardXP(rewardValue: number) {
  try {
    const resp = await new Client({
      path: "/challenges/get-converted-reward-xp",
      payload: { input: { rewardValue } },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function createChallenge(challengeDetails: any) {
  try {
    const resp = await new Client({
      path: "/challenges/create",
      payload: challengeDetails,
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

async function updateChallenge(challengeDetails: any, challengeId: any) {
  try {
    const resp = await new Client({
      path: "/challenges/update",
      payload: { ...challengeDetails, _id: challengeId },
    }).post();
    return resp;
  } catch (error) {
    throw error;
  }
}

export async function deleteChallenge(challengeId: any) {
  const resp = await new Client({
    path: "/challenges/delete",
    payload: { challengeId },
  }).post();
  return resp;
}

export async function updateChallengeStatus(challengeId: any) {
  const resp = await new Client({
    path: "/challenges/update/status",
    payload: { challengeId },
  }).post();
  return resp;
}

// ─── Bulk upload ─────────────────────────────────────────────────────────

/** Uploads an "Individual" or "Combo" bulk-create XLSX. type = "individual" | "combo". */
export async function bulkUploadChallenges(
  type: "individual" | "combo",
  file: File,
) {
  const formData = new FormData();
  formData.append("file", file);

  const resp = await new Client({
    path: `/challenges/bulk/${type}`,
    payload: formData,
    isFormData: true,
  }).post();
  return resp;
}

/** Downloads the bulk-create XLSX template. type = "individual" | "combo". */
export async function downloadChallengeTemplate(type: "individual" | "combo") {
  const url = `${import.meta.env.VITE_CMS_API_URL}/challenges/bulk/template/${type}`;

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${sessionHelper?.token}` },
  });

  if (!response.ok) {
    throw new Error("Failed to download the template. Please try again.");
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename=([^;]+)/);
  const filename = match
    ? match[1].trim()
    : `Bulk-Create-${type}-Challenges-Template.xlsx`;

  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

export async function getChallengePrizeWheels(options: any = {}) {
  const payload: any = {
    pageNo: options?.pageNo ?? 1,
    limit: options?.pageSize ?? 50,
    sortKey: options?.sortKey ?? "createdAt",
    sortDir: options?.sortDir ?? "DESC",
    skipPagination: options?.skipPagination ?? false,
  };
  if (options?.search) payload.search = options.search;
  if (options?.isActive !== undefined) payload.isActive = options.isActive;

  return new Client({ path: "/challenges/prize-wheels/list", payload }).post();
}

export async function getChallengePrizeWheelInfo(prizeWheelConfigId: string) {
  return new Client({
    path: "/challenges/prize-wheels/info",
    payload: { prizeWheelConfigId },
  }).post();
}

export async function createChallengePrizeWheel(input: any) {
  return new Client({
    path: "/challenges/prize-wheels/create",
    payload: input,
  }).post();
}

export async function updateChallengePrizeWheel(input: any) {
  return new Client({
    path: "/challenges/prize-wheels/update",
    payload: input,
  }).post();
}

export async function deleteChallengePrizeWheel(prizeWheelConfigId: string) {
  return new Client({
    path: "/challenges/prize-wheels/delete",
    payload: { prizeWheelConfigId },
  }).post();
}

export async function copyChallenge(challengeId: any) {
  const resp = await new Client({
    path: `/challenges/copy`,
    payload: { input: { challengeId } },
  }).post();

  return resp;
}

export { getChallenges, getChallengeInfo, createChallenge, updateChallenge };

export async function archiveChallenges(ids: string[]) {
  const resp = await new Client({
    path: "/challenges/archive",
    payload: { ids },
  }).post();
  return resp;
}

export async function unarchiveChallenges(ids: string[]) {
  const resp = await new Client({
    path: "/challenges/unarchive",
    payload: { ids },
  }).post();
  return resp;
}
